/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */

import Product from '../../domains/Product'
import QuinceApiError from '../../domains/quince-api-error'
import AuthUtil from '../../utils/authUtil'
import { storefrontCheckoutServicesHttpClient } from '../../utils/HttpClient'
import { appendEDDExpBucketToHeaders } from '../eddService/eddService.helper'
import { fetchAndEnrichProducts } from '../productService/fetchAndEnrichProducts'

import { getCheckoutId, formatToYYYYMMDD } from './cartCheckoutService.helper'
import {
  getDataWithProductAndInventory,
  removeCheckoutIdFromLocalStorage,
  setCartAuthCodeFromHeader,
} from './checkout.service.helper'
import { Cart, GiftingDetails, LineItems, TBuyNowApiResponse } from './checkout.service.types'

export const getCheckoutWithId = async (checkoutId: string): Promise<any> => {
  try {
    const res = await storefrontCheckoutServicesHttpClient.get(`/checkouts/${checkoutId}`)
    setCartAuthCodeFromHeader(res)
    const { data } = res
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    removeCheckoutIdFromLocalStorage()
    console.error('Failed while fetching getCheckoutWithId(), API - `/checkouts/${checkoutId}`', e)
  }
  return {}
}

export const getProductData = async ({ productIds }: { productIds: (number | string)[] }): Promise<Product[]> => {
  const products = await fetchAndEnrichProducts({
    internalProductIds: productIds,
  })
  return products
}

export const getIsCartActive = async (): Promise<any> => {
  try {
    const userSession = await AuthUtil.getUserSession()
    if (!userSession?.idToken?.jwtToken) return
    const res = await storefrontCheckoutServicesHttpClient.get(`/checkouts/active`, {
      headers: {
        Authorization: `Bearer ${userSession?.idToken?.jwtToken}`,
      },
    })
    setCartAuthCodeFromHeader(res)
    const { data } = res
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching getIsCartActive(), API - `/carts/active`', e)
    // throw e
  }
}

export const mergeCustomerCart = async (previousCartId: string): Promise<any> => {
  try {
    const userSession = await AuthUtil.getUserSession()
    const { data } = await storefrontCheckoutServicesHttpClient.post(
      `/checkouts/${previousCartId}/merge-customer-checkout`,
      {
        headers: {
          Authorization: `Bearer ${userSession?.idToken?.jwtToken}`,
        },
      },
    )
    return await getDataWithProductAndInventory(data?.second)
  } catch (e) {
    console.error('Failed while fetching mergeCustomerCart(), API - `/merge-customer-checkout`', e)
    return {}
  }
}

export const getCheckouts = async (
  lineItems: LineItems,
  previousCheckoutId?: string,
  isDOBRequired?: boolean,
  dateOfBirth?: string,
): Promise<Cart | undefined> => {
  try {
    const requestBody = {
      lineItems,
      ...(isDOBRequired && dateOfBirth && { customerDateOfBirth: formatToYYYYMMDD(dateOfBirth) }),
    }
    const res = await storefrontCheckoutServicesHttpClient.post(
      previousCheckoutId
        ? `/checkouts/create-for-line-items?previousCheckoutId=${previousCheckoutId}`
        : `/checkouts/create-for-line-items`,
      requestBody,
    )
    setCartAuthCodeFromHeader(res)
    const { data } = res

    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching getCheckouts(), API - `/checkouts/create-for-line-items`', e)
  }
  return
}

export const getPreviousCheckouts = async (previousCheckoutId: string): Promise<Cart | undefined> => {
  try {
    const res = await storefrontCheckoutServicesHttpClient.post(
      `/checkouts/create-checkout-if-possible/${previousCheckoutId}`,
      {},
    )
    setCartAuthCodeFromHeader(res)
    const { data } = res
    if (data.createdCheckout) {
      return await getDataWithProductAndInventory(data.checkout)
    }
  } catch (e) {
    console.error(
      'Failed while fetching getPreviousCheckouts(), API - `/checkouts/create-checkout-if-possible/${previousCheckoutId}`',
      e,
    )
  }
}

export const updateItemQuantity = async (
  checkoutId: string,
  lineItems: {
    lineItemId: string | null | undefined
    adjustment: number
  }[],
): Promise<any> => {
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(
      `/checkouts/${checkoutId}/cart/update-items-quantity`,
      {
        lineItems,
      },
    )
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error(
      'Failed while fetching updateItemQuantity(), API - `/checkouts/${checkoutId}/cart/update-item-quantity`',
      e,
    )
  }
  return ''
}

export const removeItem = async (_cartId: string, lineItemId: string): Promise<string> => {
  const checkoutId = getCheckoutId()
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(`/checkouts/${checkoutId}/cart/remove-item`, {
      lineItemId,
    })
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching removeItemQyantity(), API - `/checkouts/${checkoutId}/cart/remove-item', e)
  }
  return ''
}

export const removeDiscountCode = async (_cartId: string): Promise<string> => {
  const checkoutId = getCheckoutId()
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(
      `/checkouts/${checkoutId}/cart/remove-discount-code`,
      {},
    )
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error(
      'Failed while fetching removeDiscountCode(), API - `/checkouts/${checkoutId}/cart/remove-discount-code',
      e,
    )
  }
  return ''
}

export const markCartComplete = async (_cartId: string): Promise<string> => {
  const checkoutId = getCheckoutId()
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(
      `/checkouts/${checkoutId}/cart/mark-cart-complete`,
      {},
    )
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error(
      'Failed while fetching markCartComplete(), API - `/checkouts/${checkoutId}/cart/mark-cart-complete',
      e,
    )
  }
  return ''
}

export const applyDiscountCode = async (checkoutId: string, discountCode: string): Promise<string> => {
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(
      `/checkouts/${checkoutId}/cart/apply-discount-code`,
      {
        discountCode,
      },
    )
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error(
      'Failed while fetching applyDiscountCode(), API - `/checkouts/${checkoutId}/cart/apply-discount-code',
      e,
    )
  }
  return ''
}

export const addItemToCart = async (lineItems: LineItems) => {
  const checkoutId = getCheckoutId()
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(`/checkouts/${checkoutId}/cart/add-item`, {
      lineItems,
    })

    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching addItemToCart(), API - `/checkouts/${checkoutId}/cart/add-item`', e)
  }
  return ''
}

export const updateDateOfBirth = async (dob: string) => {
  const checkoutId = getCheckoutId()
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(
      `/checkouts/${checkoutId}/cart/update-date-of-birth`,
      {
        dateOfBirth: formatToYYYYMMDD(dob),
      },
    )

    return data
  } catch (e) {
    console.error('Failed while updating date of birth, API - `/checkouts/${checkoutId}/cart/update-date-of-birth`', e)
    const quinceError = new QuinceApiError({
      error: e as Error,
      message: 'Failed on updateDateOfBirth',
      apiName: 'DOB_ERROR',
    })
    throw quinceError
  }
}

export const buyNowCartApi = async (
  lineItems: LineItems,
  parentCartAuthMapping?: {
    id: string
    authCode: string
  },
): Promise<TBuyNowApiResponse | undefined> => {
  try {
    const checkoutId = getCheckoutId()
    const { data } = await storefrontCheckoutServicesHttpClient.post(`/checkouts/${checkoutId}/cart/buy-now-cart`, {
      lineItems,
      parentCartAuthMapping,
    })
    return data
  } catch (e) {
    console.error('Failed while fetching buyNowCart(), API - `/carts/buy-now-cart`', e)
  }
  return
}

export const updateGiftingDetails = async (giftingDetails: GiftingDetails): Promise<any> => {
  const checkoutId = getCheckoutId()
  try {
    const res = await storefrontCheckoutServicesHttpClient.put(`/checkouts/${checkoutId}/cart/update-gifting-details`, {
      recipientEmail: giftingDetails.recipientEmail,
      recipientName: giftingDetails.recipientName,
      senderName: giftingDetails.senderName,
      giftMessage: giftingDetails.giftMessage,
    })
    setCartAuthCodeFromHeader(res)
    const { data } = res
    return await getDataWithProductAndInventory(data, false)
  } catch (e) {
    console.error('Failed while updating gifting API - `/update-gifting-details`', e)
    throw e
  }
}

export const updateGiftingFlag = async (isGift: boolean): Promise<any> => {
  const checkoutId = getCheckoutId()
  try {
    const res = await storefrontCheckoutServicesHttpClient.put(
      `/checkouts/${checkoutId}/cart/update-gifting-flag?isGift=${isGift}`,
      {},
    )
    setCartAuthCodeFromHeader(res)
    const { data } = res
    return await getDataWithProductAndInventory(data, false)
  } catch (e) {
    console.error('Failed while updating gifting API - `/update-gifting-flag`', e)
    throw e
  }
}

export const removeAllOutOfStockItemsFromCart = async (): Promise<any> => {
  const checkoutId = getCheckoutId()
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(
      `/checkouts/${checkoutId}/cart/remove-all-out-of-stock-items`,
      {},
    )
    const cart = await getDataWithProductAndInventory(data, false)
    return cart
  } catch (e) {
    console.error(
      'Failed while removing all out of stock items API - `/checkouts/${checkoutId}/cart/remove-all-out-of-stock-items`',
      e,
    )
    throw e
  }
}

// save for later

export const saveItemForLater = async (
  cartId: string,
  saveForLaterLineItems: {
    lineItemId: string | null | undefined
  }[],
): Promise<any> => {
  const checkoutId = getCheckoutId()

  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(
      `/checkouts/${checkoutId}/cart/save-items-for-later`,
      {
        saveForLaterLineItems,
      },
    )
    const cart = await getDataWithProductAndInventory(data)
    return cart
  } catch (e) {
    console.error(
      'Failed while fetching saveItemForLater(), API - `/checkouts/${checkoutId}/cart/save-items-for-later`',
      e,
    )
  }
  return ''
}

export const moveItemBackToCart = async (
  cartId: string,
  moveToCartLineItems: {
    lineItemId: string | null | undefined
  }[],
): Promise<any> => {
  const checkoutId = getCheckoutId()
  try {
    const { data } = await storefrontCheckoutServicesHttpClient.put(
      `/checkouts/${checkoutId}/cart/move-items-to-cart`,
      {
        moveToCartLineItems,
      },
    )
    const cart = await getDataWithProductAndInventory(data)
    return cart
  } catch (e) {
    console.error(
      'Failed while fetching moveItemBackToCart(), API - `/checkouts/${checkoutId}/cart/move-items-to-cart`',
      e,
    )
  }
  return ''
}

// is checkout active

export const getIsCheckoutActive = async (): Promise<any> => {
  try {
    const userSession = await AuthUtil.getUserSession()
    if (!userSession?.idToken?.jwtToken) return
    const res = await storefrontCheckoutServicesHttpClient.get(`/checkouts/active`, {
      headers: {
        Authorization: `Bearer ${userSession?.idToken?.jwtToken}`,
      },
    })
    setCartAuthCodeFromHeader(res)
    const { data } = res
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching getIsCheckoutActive(), API - `/checkouts/active`', e)
    // throw e
  }
}

export const refreshFulfillmentDetails = async (
  zipCode: string,
  checkoutId: string | null = getCheckoutId(),
  ignoreIfNotExpired?: boolean,
): Promise<any> => {
  try {
    if (!zipCode) {
      return
    }

    if (!checkoutId) {
      console.warn('Checkout ID is not available')
      return
    }

    const eddHeaders = appendEDDExpBucketToHeaders()

    const { data } = await storefrontCheckoutServicesHttpClient.post(
      `/checkouts/${checkoutId}/refresh-cart-fulfillment-package`,
      {
        zipCode,
        ignoreIfNotExpired,
      },
      {
        headers: eddHeaders,
      },
    )

    return data
  } catch (e) {
    console.error(
      'Failed while fetching refreshFulfillmentDetails(), API - `/checkouts/${checkoutId}//checkouts/{checkoutId}/refresh-cart-fulfillment-package`',
      e,
    )
  }
}
